/**
 * Frontend mirror of `backend/src/middleware/permissions.ts`.
 *
 * Keep in lockstep with the backend matrix. The shape of `Action` and
 * `PERMISSIONS` matches the server's exactly; the helpers below let
 * `useCan()` evaluate whether the current user can perform an action at a
 * concrete `{ scopeType, scopeId }` given the user's memberships.
 *
 * Inheritance rule (same as backend): admin at a parent scope inherits
 * admin at every child scope (institution → chapter → branch).
 */

import type { ScopeRole, ScopeType } from '@/services/api';

export type Action =
  | 'institution:create-chapter'
  | 'chapter:update'
  | 'chapter:create-branch'
  | 'branch:update'
  | 'chapter:invite'
  | 'branch:invite'
  | 'branch:approve-join-request'
  | 'chapter:approve-join-request'
  | 'project:create-root'
  | 'project:create-chapter'
  | 'project:create-branch'
  | 'page:edit'
  | 'gallery:upload'
  | 'gallery:create-share-link'
  | 'view-private';

interface ActionRule {
  scope: ScopeType;
  minRole: ScopeRole;
}

const ROLE_RANK: Record<ScopeRole, number> = {
  read_only: 1,
  contributor: 2,
  admin: 3,
};

export const PERMISSIONS: Record<Action, ActionRule> = {
  'institution:create-chapter':   { scope: 'institution', minRole: 'admin' },
  'chapter:update':               { scope: 'chapter',     minRole: 'admin' },
  'chapter:create-branch':        { scope: 'chapter',     minRole: 'admin' },
  'branch:update':                { scope: 'branch',      minRole: 'admin' },
  'chapter:invite':               { scope: 'chapter',     minRole: 'admin' },
  'branch:invite':                { scope: 'branch',      minRole: 'admin' },
  'branch:approve-join-request':  { scope: 'branch',      minRole: 'admin' },
  'chapter:approve-join-request': { scope: 'chapter',     minRole: 'admin' },
  'project:create-root':          { scope: 'institution', minRole: 'admin' },
  'project:create-chapter':       { scope: 'chapter',     minRole: 'admin' },
  'project:create-branch':        { scope: 'branch',      minRole: 'admin' },
  'page:edit':                    { scope: 'branch',      minRole: 'admin' },
  'gallery:upload':               { scope: 'branch',      minRole: 'contributor' },
  'gallery:create-share-link':    { scope: 'branch',      minRole: 'admin' },
  'view-private':                 { scope: 'branch',      minRole: 'read_only' },
};

export function roleSatisfies(actual: ScopeRole, required: ScopeRole): boolean {
  return ROLE_RANK[actual] >= ROLE_RANK[required];
}

/** A single membership the user holds (institution / chapter / branch). */
export interface HeldMembership {
  scopeType: ScopeType;
  scopeId: string;
  role: ScopeRole;
}

/**
 * Returns true when the user holds the required role at the requested
 * scope — directly OR via admin inheritance from an ancestor scope.
 *
 * `ancestors` lists ancestor scopes of the target (e.g. for a branch,
 * the parent chapter and the institution).
 */
export function hasRoleAtOrAbove(
  held: HeldMembership[],
  target: { scopeType: ScopeType; scopeId: string },
  required: ScopeRole,
  ancestors: { scopeType: ScopeType; scopeId: string }[] = [],
): boolean {
  // Direct membership
  for (const m of held) {
    if (m.scopeType === target.scopeType && m.scopeId === target.scopeId && roleSatisfies(m.role, required)) {
      return true;
    }
  }
  // Admin inheritance from ancestors (only admin role inherits downward)
  for (const a of ancestors) {
    for (const m of held) {
      if (m.scopeType === a.scopeType && m.scopeId === a.scopeId && m.role === 'admin') return true;
    }
  }
  return false;
}

/**
 * Resolve whether `held` memberships can perform `action` at a concrete
 * scope `{ scopeType, scopeId }` (the scope name in the URL).
 *
 * - If the action's rule scope differs from the target scope (e.g. the
 *   action is `branch:invite` but the target is a chapter), the answer is
 *   no — the caller is asking the wrong question.
 * - Otherwise: check direct membership and ancestor-admin inheritance.
 */
export function canActOn(
  held: HeldMembership[],
  action: Action,
  target: { scopeType: ScopeType; scopeId: string },
  ancestors: { scopeType: ScopeType; scopeId: string }[] = [],
): boolean {
  const rule = PERMISSIONS[action];
  if (rule.scope !== target.scopeType) return false;
  return hasRoleAtOrAbove(held, target, rule.minRole, ancestors);
}
